import { useState } from 'react';
import type { VideoCard } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { Icon } from '@/components/shell';
import { cn } from '@/components/ui';

/**
 * The player — PRD §13, ADR-006: every video is somebody else's iframe, and nothing is
 * self-hosted. Three sources, one component:
 *
 * | `source`        | rendered as                        | aspect |
 * | --------------- | ---------------------------------- | ------ |
 * | `youtube`       | `youtube-nocookie.com/embed/<id>`  | 16:9   |
 * | `youtube_short` | the same embed, portrait frame     | 9:16   |
 * | `embed`         | the editor's URL, used as given    | 16:9   |
 *
 * **Nothing loads until the reader asks.** The page ships a poster and a button; the
 * iframe is created on click. That is the whole point of the facade pattern: a YouTube
 * embed costs upwards of half a megabyte of script and sets cookies before a reader has
 * decided to watch anything, and a grid page that autoloaded four of them would be
 * slower than the video it is trying to show. The Phase 9 body renderer does the same
 * thing to embeds inside article HTML.
 *
 * `youtube-nocookie.com` rather than `youtube.com`: same player, no cookie until play.
 */
export function VideoPlayer({ video, onPlay }: { video: VideoCard; onPlay?: () => void }) {
  const { t } = useLocale();
  const [playing, setPlaying] = useState(false);
  const short = video.source === 'youtube_short';
  const src = embedSrc(video);

  const frame = cn(
    'relative overflow-hidden rounded-lg bg-black',
    // A Short is portrait. Letterboxing one into a 16:9 box is the failure this phase's
    // DoD names explicitly, so the frame follows the format and the width is capped
    // instead — a 9:16 video at full column width would be taller than the screen.
    short ? 'mx-auto aspect-[9/16] w-full max-w-[380px]' : 'aspect-video w-full',
  );

  if (!src) {
    return (
      <div className={cn(frame, 'grid place-items-center')}>
        <p className="px-6 text-center text-sm text-white/80">{t('video.unavailable')}</p>
      </div>
    );
  }

  if (playing) {
    return (
      <div className={frame}>
        <iframe
          src={`${src}${src.includes('?') ? '&' : '?'}autoplay=1`}
          title={video.title}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <div className={frame}>
      <Poster video={video} />
      {/*
        No `autoFocus`: the play button is the first thing in the page's content anyway,
        and moving focus on load scrolls a reader who arrived with the keyboard to
        wherever the player happens to be.
      */}
      <button
        type="button"
        data-video-play
        onClick={() => {
          setPlaying(true);
          onPlay?.();
        }}
        className="absolute inset-0 grid place-items-center bg-black/10 transition hover:bg-black/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
      >
        <span className="grid h-16 w-16 place-items-center rounded-full bg-white/90 shadow-float transition group-hover:scale-105">
          <Icon name="play" size={24} className="ml-1 text-red" />
        </span>
        <span className="sr-only">{t('action.play')}</span>
      </button>
    </div>
  );
}

/**
 * The still behind the play button: the editor's uploaded override if there is one, the
 * provider's thumbnail otherwise, and a plain black frame when a generic embed has
 * given us neither.
 */
function Poster({ video }: { video: VideoCard }) {
  const src = video.thumbnail
    ? `/media/${video.thumbnail.variants.find((variant) => variant.format === 'jpeg')?.storagePath ?? video.thumbnail.storagePath}`
    : (video.thumbnailUrl ?? youtubePoster(video));

  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      // The poster is the page's largest image, and the reader is looking straight at it.
      fetchPriority="high"
      decoding="async"
      className="absolute inset-0 h-full w-full object-cover"
    />
  );
}

/** `youtube-nocookie` embed URL, or the editor's own URL for a generic embed. */
export function embedSrc(video: VideoCard): string | null {
  if (video.source === 'embed') return video.sourceUrl;
  if (!video.externalId) return video.sourceUrl;
  return `https://www.youtube-nocookie.com/embed/${video.externalId}`;
}

function youtubePoster(video: VideoCard): string | null {
  if (video.source === 'embed' || !video.externalId) return null;
  // `hqdefault` exists for every video; `maxresdefault` does not, and a missing one is a
  // grey 404 image rather than a fallback.
  return `https://i.ytimg.com/vi/${video.externalId}/hqdefault.jpg`;
}
